import { User, UserRole, canAccessManagement, canAccessPublic } from '@/domain/auth/auth'
import { create, StateCreator } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import { useTokenStore } from './useTokenStore'
import { cookieStorage } from '@/utils/cookies'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  showSessionExpired: boolean

  // Actions
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setShowSessionExpired: (show: boolean) => void
  login: (user: User, token: string) => void
  logout: () => void

  // Computed getters
  canAccessManagement: () => boolean
  canAccessPublic: () => boolean
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
}

const authStore: StateCreator<AuthState, [["zustand/devtools", never], ["zustand/persist", unknown]], []> = (set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  showSessionExpired: false,

  setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  setShowSessionExpired: (show: boolean) => set({ showSessionExpired: show }),

  login: (user: User, token: string) => {
    useTokenStore.getState().setToken(token)
    set({ user, isAuthenticated: true })
  },

  logout: () => {
    useTokenStore.getState().clearToken()
    set({ user: null, isAuthenticated: false })
  },

  // Getters
  canAccessManagement: () => get().user ? canAccessManagement(get().user) : false,
  canAccessPublic: () => get().user ? canAccessPublic(get().user) : false,
  hasRole: (role: UserRole) => get().user?.roles?.includes(role) ?? false,
  hasAnyRole: (roles: UserRole[]) => roles.some(role => get().hasRole(role))
})

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      authStore,
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => cookieStorage),
      }
    ),
    { name: 'AuthStore' }
  )
)

// Helper hook to get the token
export const useAuthToken = () => useTokenStore(state => state.token)